<script>
  import { onMount } from "svelte";
  import {
    Page,
    Navbar,
    NavLeft,
    NavTitle,
    NavTitleLarge,
    NavRight,
    Link,
    Block,
    BlockTitle,
    Button,
    Icon,
    Searchbar,
    Card,
    CardContent,
    CardHeader,
    f7,
    f7ready,
  } from "framework7-svelte";
  import { fetchSystemEvolutionDocs } from "../js/navigationStore";

  export let f7route = undefined;
  export let f7router = undefined;

  function navigateTo(url) {
    if (!url) return;
    try {
      if (f7router && typeof f7router.navigate === "function") {
        f7router.navigate(url);
        return;
      }
      if (f7 && f7.views && f7.views.main && f7.views.main.router) {
        f7.views.main.router.navigate(url);
        return;
      }
      if (f7 && f7.view && f7.view.main && f7.view.main.router) {
        f7.view.main.router.navigate(url);
        return;
      }
    } catch (e) {
      console.warn("Navigation failed, falling back to window location", e);
    }
    window.location.href = url;
  }

  // View Mode: 'boss' (Executive View) | 'tech' (Technical / Developer View)
  let viewMode = "boss";
  let selectedCategory = "all";
  let searchQuery = "";
  let loading = false;
  let lightboxImage = null;

  // Fallback initial SSOT data
  let metadata = {
    title: "TRDR Back-Office Operations: Manual vs. Automated System Guide",
    subtitle:
      "Complete Operational Guide to User Approvals, FCML Broker Integration, Demat Allocation & Payment Audits",
    short_title: "Broker & Back-Office Ops",
    version: "v2.1.0",
    source_doc: "How to_ TRDR Admin API's (1).docx",
    last_updated: "2026-10-27",
    executive_summary:
      "Comprehensive executive overview and technical audit trail detailing the automation of TRDR back-office workflows. Eliminates developer WhatsApp bottlenecks, eliminates CDSL arithmetic errors, isolates test account payments, and delivers 100% zero-trust PII data governance.",
    total_tasks: 7,
  };

  let scorecards = [
    {
      label: "Zero-Trust PII Protection",
      value: "100%",
      change: "+100%",
      direction: "up",
      description: "No plaintext admin tokens in URLs; zero reliance on 3rd-party web decoders.",
      category: "Security & Privacy",
    },
    {
      label: "Demat Arithmetic Errors",
      value: "0%",
      change: "-100%",
      direction: "down",
      description: "16-digit CDSL Demat synthesis eliminates missing zeros and BOID rejections.",
      category: "Depository Accuracy",
    },
    {
      label: "Ops Turnaround Time",
      value: "95% Cut",
      change: "-95%",
      direction: "down",
      description: "Average onboarding turnaround slashed from 20 minutes to 15 seconds.",
      category: "Operational Efficiency",
    },
    {
      label: "Financial Audit Precision",
      value: "100%",
      change: "+100%",
      direction: "up",
      description: "Test accounts blacklisted from gross settlement totals; RFC-4180 CSV exports.",
      category: "Reconciliation & Audit",
    },
  ];

  let chapters = [
    {
      id: "auth-zero-trust",
      task_number: 1,
      title: "Authentication & Zero-Trust PII Protection",
      category: "Security & Compliance",
      tagline: "Eliminating plain-text JWT tokens from browser URLs and banning unencrypted third-party decoding sites",
      executive_summary:
        "Legacy instructions required passing JWT admin tokens in URL query strings and pasting confidential customer KYC data (Aadhaar, PAN, name) into public sites like url-encode-decode.com. Today, zero-trust token authentication via HTTP headers and in-memory URL decoding ensure complete DPDP & SEBI compliance with zero data leakage.",
      technical_summary:
        "Migrated from query string authentication to Authorization Bearer headers. Built pure in-memory zero-trust URLDecoder (URLDecoder.ts) with strict sanitization, eliminating any third-party online decoding exposure.",
      risk_level_before: "CRITICAL",
      risk_level_now: "ZERO-RISK",
      turnaround_before: "5 - 10 mins",
      turnaround_now: "Instant (< 1s)",
      compliance_tags: ["DPDP Act 2023", "SEBI Cybersecurity", "Zero-Trust Architecture"],
      before: {
        headline: "Admin tokens in browser URLs & customer data pasted on external websites",
        description:
          "Old operations manuals explicitly advised operators to append ?token=eyJ... to URLs and use public website url-encode-decode.com to decode customer profile details, exposing production auth credentials to browser history, proxy logs, and external web hosts.",
        pain_points: [
          "Admin tokens leaked into browser history, proxies, and web server access logs",
          "Confidential customer PAN, Aadhaar, and bank details sent to external conversion websites",
          "Session hijacking vulnerability with tokens valid up to 24 hours in plaintext",
          "Complete non-compliance with SEBI and DPDP customer privacy frameworks",
        ],
        image_url: "",
        image_caption: "Manual token passing and third-party web decoding in legacy operations",
        code_snippet: "GET /admin?x-trdr-org=60e6...&token=eyJhbGciOi...\n// Operator manually visited url-encode-decode.com to decode user PAN/DOB",
      },
      now: {
        headline: "Encrypted session headers & local, isolated zero-trust decoding",
        description:
          "Tokens are stored in protected localStorage and transmitted strictly via HTTP headers. Customer profile decoding is processed locally in-memory using our verified URLDecoder engine without any external network requests.",
        key_improvements: [
          "100% in-memory local URL decoding with zero external API calls",
          "Authorization bearer headers for all microservices, keeping URLs clean of credentials",
          "Automatic token expiration validation with instant session termination",
          "Role-based permission gating (org_admin, dealer, executive) on every endpoint",
        ],
        screenshot_route: "/admin-login-otp/",
        live_route: "/admin-home/",
        action_label: "View Admin Overview",
        tech_stack: ["TypeScript", "URLDecoder.ts", "JWT Bearer Auth", "HTTPOnly Sessions"],
        code_snippet: "const decoded = sanitizeUserProfile(rawEncodedData);\n// Processed 100% locally in-memory inside URLDecoder.ts without network leaks",
      },
    },
    {
      id: "user-approval-demat",
      task_number: 2,
      title: "User Approval & 16-Digit CDSL Demat Allocation",
      category: "Account Operations",
      tagline: "Automating 16-digit CDSL Demat synthesis to eliminate dangerous mental arithmetic errors",
      executive_summary:
        "Manual customer approvals forced back-office staff to search across entire databases and calculate 16-digit CDSL account IDs in their heads (DP ID 12088000 + client ID), causing frequent 13-digit truncations and failed broker trades. Now, automated targeted mobile lookup and instant 16-digit synthesis guarantee 100% mathematical accuracy.",
      technical_summary:
        "add_approve_user_by_admin resolver now combines DP ID with 8-digit client code with zero padding, validates checksums, and updates member state atomically with pre-flight safety diffs.",
      risk_level_before: "HIGH",
      risk_level_now: "ZERO-RISK",
      turnaround_before: "8 - 15 mins / user",
      turnaround_now: "5 seconds / user",
      compliance_tags: ["CDSL Depository Guidelines", "SEBI UCC Mapping", "Pre-Flight Validation"],
      before: {
        headline: "Manual database searching and mental arithmetic for CDSL account IDs",
        description:
          "Operators had to load all pending users, use browser CTRL+F to find a user's record, and manually combine the 8-digit broker DP ID (12088000) with the client's internal ID. Mental arithmetic frequently led to missing zeros, 13-digit or 24-digit corruptions, and rejected trades.",
        pain_points: [
          "Human mental math errors when joining DP ID 12088000 + client ID",
          "Corrupted BOID values stored in CDSL depository led to severe trade rejections",
          "Sluggish workflow requiring searching through hundreds of records in raw lists",
          "No verification diff before permanently committing approval to the database",
        ],
        image_url: "",
        image_caption: "Legacy manual arithmetic joining 12088000 and 8-digit client codes",
        code_snippet: "// Legacy Manual Step: Operator mentally joins:\n// DP ID: 12088000 + Client Code: 00123456 => 1208800000123456 (often mistyped!)",
      },
      now: {
        headline: "Automated 16-digit synthesis with targeted mobile search and safety diffs",
        description:
          "The Approve User screen provides instant single-user mobile lookup, automatically synthesizes and validates the 16-digit CDSL Demat BOID, and displays an interactive pre-approval comparison diff before committing.",
        key_improvements: [
          "Deterministic 16-digit CDSL Demat generation with exact 8-digit zero-padded client codes",
          "Instant targeted mobile search replacing slow full-table scans",
          "Visual pre-flight approval diff highlighting UCC, Demat, and KYC status",
          "Immediate eSign and welcome dispatch upon approval confirmation",
        ],
        screenshot_route: "/approve-account/",
        live_route: "/approve-account/",
        action_label: "Open User Approval Portal",
        tech_stack: ["CDSL Synthesizer", "GraphQL fetch_user_by_mobile", "add_approve_user_by_admin"],
        code_snippet: "const cdsl_demat_account_number = `${DP_ID}${clientId.padStart(8, '0')}`;\nassert(cdsl_demat_account_number.length === 16);",
      },
    },
    {
      id: "fcml-broker-onboard",
      task_number: 3,
      title: "Direct FCML Broker Onboarding",
      category: "Broker Integration",
      tagline: "Ending developer-bottlenecked WhatsApp requests with an integrated self-service onboarding engine",
      executive_summary:
        "Whenever existing FCML broker clients needed to be provisioned in TRDR, operators had to message senior developers on WhatsApp, requesting custom GraphQL scripts and manual database injections. We replaced this bottleneck with an intuitive self-service portal featuring real-time Indian mobile and IFSC validation.",
      technical_summary:
        "add_existing_users resolver with atomic deduplication, Cashfree IFSC bank branch lookup, and RFC-5322 email validation, completely replacing manual developer mutation queries.",
      risk_level_before: "CRITICAL",
      risk_level_now: "LOW-RISK",
      turnaround_before: "2 - 6 hours (developer availability)",
      turnaround_now: "30 seconds",
      compliance_tags: ["Broker Onboarding SLA", "SEBI KYC Master", "Atomic Deduplication"],
      before: {
        headline: "WhatsApp messaging to developers & manual GraphQL mutation injection",
        description:
          "As documented in our actual operations archive (manual-ops-whatsapp.png), operators had to contact engineering lead Amal on WhatsApp to onboard broker accounts. Amal would manually craft a GraphQL add_existing_users mutation snippet with hardcoded IDs and admin tokens.",
        pain_points: [
          "Severe operational dependency on senior engineering staff during business hours",
          "Real-world evidence of WhatsApp chats sharing raw GraphQL mutations and credentials",
          "High risk of human typographical error entering PAN, mobile, or UCC directly into mutations",
          "No real-time bank IFSC verification, leading to failed payouts and bounce fees",
        ],
        image_url: "/static/docs-assets/manual-ops-whatsapp.png",
        image_caption: "Actual evidence from legacy ops doc: Operator asking Amal on WhatsApp for GraphQL scripts",
        code_snippet: 'mutation {\n  add_existing_users(users: [{ ucc: "TR1092", name: "Client Name", mobile: "9876543210" }]) {\n    status\n  }\n}',
      },
      now: {
        headline: "Standardized back-office onboarding portal with live IFSC bank validation",
        description:
          "Back-office operators can directly onboard any existing FCML client in seconds. The UI verifies Indian phone number patterns, auto-resolves bank names from IFSC via Cashfree Bank Code APIs, and guarantees zero duplicate UCC conflicts.",
        key_improvements: [
          "Zero developer intervention required; back-office operates 100% autonomously",
          "Live bank name and branch verification upon IFSC input via Cashfree Bank API",
          "Instant duplicate detection for existing UCC, mobile, and PAN records",
          "Real-time visual feedback and audit-logged onboarding activity",
        ],
        screenshot_route: "/approve-account/",
        live_route: "/approve-account/",
        action_label: "Launch FCML Onboarding",
        tech_stack: ["add_existing_users GQL", "Cashfree IFSC Lookup", "Duplicate Collision Detector"],
        code_snippet: "await gql.query('add_existing_users', { users: [validatedClientPayload] });\n// Validated IFSC branch, PAN checksum, and unique UCC",
      },
    },
    {
      id: "cashfree-audit-blacklist",
      task_number: 4,
      title: "Cashfree Payment Auditing & Blacklist Filtering",
      category: "Financial Reconciliation",
      tagline: "Separating real customer fund flows from developer test accounts for spotless financial audits",
      executive_summary:
        "Back-office payment audits were previously plagued by dummy transactions from internal test accounts (e.g. Amal Rag C, Ricky Jacob), which heavily distorted total transaction volumes and cash collections. We engineered an automated test-account filtering engine with 1-click RFC-4180 CSV export for clean auditor reconciliation.",
      technical_summary:
        "fetch_cashfree_transactions resolver with test-account regex filters, dynamic date-range aggregation, and instant CSV generation conforming to financial audit standards.",
      risk_level_before: "HIGH",
      risk_level_now: "ZERO-RISK",
      turnaround_before: "1 - 2 hours",
      turnaround_now: "3 seconds",
      compliance_tags: ["Financial Audit Trail", "RFC-4180 CSV", "Cashfree Gateway Reconciliation"],
      before: {
        headline: "Polluted transaction ledgers contaminated by developer test transactions",
        description:
          "Reconciling payment gateway collections with bank statements required tedious manual skimming through thousands of Cashfree transactions, manually picking out test payments made by engineers and QA testers during release cycles.",
        pain_points: [
          "Test accounts (Amal Rag C, Ricky Jacob, etc.) distorted gross deposit and transaction totals",
          "High risk of presenting inaccurate financial metrics to external statutory auditors",
          "Manual filtering in Excel resulted in copy-paste truncation and date format inconsistencies",
          "No automated KPI rollups for settled vs failed vs pending gateway payments",
        ],
        image_url: "",
        image_caption: "Unfiltered gateway transactions showing test accounts mixed with real customer funds",
        code_snippet: "// Operator had to open Excel and manually delete rows containing:\n// 'Amal Rag C', 'Ricky Jacob', 'Test Admin' from thousands of CSV rows",
      },
      now: {
        headline: "1-Click automated test filtering with live financial metrics & audit-ready CSV",
        description:
          "Our Cashfree Transactions dashboard features a dedicated 'Hide Test Accounts' toggle, dynamic KPI cards summarizing Total Settled / Pending / Failed volume, and instant RFC-4180 compliant CSV export ready for finance teams.",
        key_improvements: [
          "Intelligent test-account blacklisting toggle active by default for all finance operators",
          "Real-time volume aggregation broken down by payment mode (UPI, NetBanking, Card)",
          "1-Click RFC-4180 compliant CSV export with proper BOM for Excel compatibility",
          "Transaction ID deep-linking to verify payment status against Cashfree webhook logs",
        ],
        screenshot_route: "/cashfree-transactions/",
        live_route: "/cashfree-transactions/",
        action_label: "Open Payment Audit Hub",
        tech_stack: ["CashfreeTransactionData", "RFC-4180 Exporter", "Audit Blacklist Filter"],
        code_snippet: "const cleanTxns = txns.filter(t => !TEST_ACCOUNTS.includes(t.customer_name));\nconst totalSettled = cleanTxns.filter(t => t.tx_status === 'SUCCESS').reduce(...);",
      },
    },
    {
      id: "homepage-health-anomaly",
      task_number: 5,
      title: "Homepage Financial Health & Capital Leak Prevention",
      category: "Risk Management",
      tagline: "Transforming raw JSON database dumps into real-time visual anomaly monitors with critical leak alerts",
      executive_summary:
        "Operations had to inspect unformatted database JSON dumps to check customer portfolio balances, missing negative cash balances and drawdown violations until after trading hours. Today, live heuristic anomaly detection highlights critical capital leaks, negative balances, and margin breaches with instant visual alert badges.",
      technical_summary:
        "get_all_users_homepage_data resolver computes live AUM, unrealized PnL, available margin, and flags CRITICAL/WARNING threshold breaches before market open.",
      risk_level_before: "CRITICAL",
      risk_level_now: "LOW-RISK",
      turnaround_before: "End-of-day discovery",
      turnaround_now: "Real-time continuous",
      compliance_tags: ["Capital Adequacy", "Margin Risk Management", "SEBI Drawdown Rules"],
      before: {
        headline: "Dense raw JSON database dumps without visual alerts or health indicators",
        description:
          "To monitor user balances, back-office staff had to browse raw JSON documents containing nested arrays of holdings, cash, and ledger entries. Margin shortfalls, negative balances, and unauthorized leverage went unnoticed until settlement.",
        pain_points: [
          "Capital leaks and negative ledger balances went unnoticed until end-of-day batch processing",
          "Operators had to mentally parse nested JSON structures across hundreds of customer accounts",
          "No automated threshold alerting for extreme client drawdowns or leverage violations",
          "Risk of regulatory penalties for failing to maintain mandatory client margin requirements",
        ],
        image_url: "",
        image_caption: "Raw JSON dump from legacy API without visual hierarchy or risk alerts",
        code_snippet: '{\n  "data": { "get_all_users_homepage_data": [{ "ucc": "TR001", "ledger_balance": "-45210.50", "holdings": [...] }] }\n}',
      },
      now: {
        headline: "Live financial health dashboard with color-coded CRITICAL/WARNING badges",
        description:
          "The Home Data monitor evaluates every account against risk heuristics in real-time. Accounts with negative balances, margin deficits, or erratic data trigger glowing CRITICAL alert badges with direct access to freeze trading or demand top-up.",
        key_improvements: [
          "Instant heuristic anomaly scanning highlighting negative cash balances and margin deficits",
          "Prominent CRITICAL (red) and WARNING (amber) visual badges for at-risk accounts",
          "Executive summary bar showing total client AUM, aggregate ledger cash, and active leverage",
          "Direct 1-click drill-down to user trading controls and order book freezes",
        ],
        screenshot_route: "/all-users-home-data/",
        live_route: "/all-users-home-data/",
        action_label: "Monitor Client Health",
        tech_stack: ["CalculateHomePage.ts", "Heuristic Anomaly Engine", "Margin Health Badges"],
        code_snippet: "if (user.ledger_balance < 0) return { anomaly: 'CRITICAL', reason: 'Negative Ledger Balance' };\nif (user.margin_utilized > 0.85) return { anomaly: 'WARNING', reason: 'High Margin Utilization' };",
      },
    },
    {
      id: "multi-report-hub",
      task_number: 6,
      title: "Multi-Report Hub & Tabular Flattening",
      category: "Back-Office Reporting",
      tagline: "Unifying four disparate reporting endpoints into a single flattened CSV and scrip export center",
      executive_summary:
        "Generating customer tax and trade reports required querying 4 separate, incompatible GraphQL endpoints (Net Position, Ledger, Portfolio, and Long/Short), each returning deeply nested scrip structures that broke spreadsheet software. We engineered a Unified Multi-Report Hub that automatically flattens all scrips, tax fees, and levies into clean tabular CSVs.",
      technical_summary:
        "fetch_all_reports resolver unifies multi-service data pipelines, flattens GST/SEBI/STT levies into columnar format, and outputs clean multi-tab audit downloads.",
      risk_level_before: "MEDIUM",
      risk_level_now: "ZERO-RISK",
      turnaround_before: "30 - 45 mins / report",
      turnaround_now: "5 seconds / report",
      compliance_tags: ["Statutory Tax Reporting", "SEBI STT / Stamp Duty Audits", "RFC-4180 CSV"],
      before: {
        headline: "Querying four separate GraphQL APIs with broken nested JSON in spreadsheets",
        description:
          "Operators had to make four independent API calls for a single user to assemble their tax pack. The responses contained nested transaction_charges objects that converted into '[object Object]' when opened in Excel, requiring manual script writing.",
        pain_points: [
          "Four separate API endpoints required to generate a single client's tax audit pack",
          "Nested JSON charges (STT, GST, Stamp Duty) broke standard CSV export tools",
          "Back-office staff constantly dependent on engineers to run custom unnesting scripts",
          "Frequent customer dispute turnaround delays due to fragmented reporting data",
        ],
        image_url: "",
        image_caption: "Fragmented reports with nested JSON objects breaking Excel exports",
        code_snippet: '// Broken CSV output from legacy unflattened data:\n// ClientCode,PnL,Charges\n// TR001,15000,[object Object]  <-- Broken data in Excel!',
      },
      now: {
        headline: "Unified report center with automated scrip and fee tabular flattening",
        description:
          "Our Unified Reports Center allows operators to select any client UCC and instantly view or export all four reports. All tax components (SEBI turnover fees, Stamp Duty, STT, State GST, Central GST) are flattened into clear individual columns.",
        key_improvements: [
          "Single consolidated interface for Net Position, Ledger Balance, Portfolio, and Long/Short",
          "100% tabular flattening of complex nested fees (STT, Stamp Duty, SEBI turnover, GST)",
          "Multi-report batch download with single-click export for tax filings and CA verifications",
          "Built-in search and filter by date, scrip exchange token, and trade direction",
        ],
        screenshot_route: "/all-reports-of-user/",
        live_route: "/all-reports-of-user/",
        action_label: "Open Reports Hub",
        tech_stack: ["Unified Report Aggregator", "Tabular Flattener", "CSVDownloader Svelte"],
        code_snippet: "const flattenedRow = {\n  ucc: row.client_code,\n  stt: row.transaction_charges?.stt || 0,\n  sebi_charges: row.transaction_charges?.sebi_charges || 0,\n  cgst: row.transaction_charges?.central_gst_on_tran_charges || 0\n};",
      },
    },
    {
      id: "digio-ekyc-dossier",
      task_number: 7,
      title: "Digio eKYC & Broker Handoff Automation",
      category: "KYC & Verification",
      tagline: "Replacing manual portal downloads with automated SHA-256 cryptographic dossiers and 1-click dispatch",
      executive_summary:
        "Operators previously had to manually log in to enterprise.digio.in with external credentials, search for applicants, download multiple PDF attachments to their local desktop, and write manual emails to broker desks. Our integrated system fetches Digio KYC dossiers directly via API, computes SHA-256 integrity hashes, and prepares 1-click broker handoffs.",
      technical_summary:
        "Direct Digio API client integration with cryptographic SHA-256 verification, S3 presigned dossier packaging, and automated broker notification webhook dispatch.",
      risk_level_before: "HIGH",
      risk_level_now: "ZERO-RISK",
      turnaround_before: "20 mins / applicant",
      turnaround_now: "15 seconds / applicant",
      compliance_tags: ["KRA KYC Regulations", "SHA-256 Integrity Check", "Zero Local Storage Exposure"],
      before: {
        headline: "Manual portal logins, local file downloads, and manual email composition",
        description:
          "Staff had to log in to third-party Digio dashboard, locate documents, save customer PAN/Aadhaar/Signature PDFs to unsecured local desktop folders, and manually attach them to outgoing emails sent to the broker onboarding desk.",
        pain_points: [
          "Customer identity PDFs saved to unsecured staff laptops, violating DPDP data rules",
          "High turnaround time (up to 20 minutes per client) during high-volume market surges",
          "Risk of sending the wrong client's KYC documents to the broker desk due to manual naming",
          "No cryptographic tamper-evidence verification of downloaded KYC documents",
        ],
        image_url: "",
        image_caption: "Manual Digio dashboard workflow requiring external logins and desktop file handling",
        code_snippet: "// Operator had to open enterprise.digio.in in browser\n// Download client_aadhaar.pdf, client_pan.pdf to local desktop\n// Open Outlook and manually write email to broker desk",
      },
      now: {
        headline: "Direct API dossier generation with SHA-256 cryptographic verification & 1-click handoff",
        description:
          "The admin portal communicates directly with Digio microservices via secure server-to-server TLS. Customer dossiers are generated in cloud storage with SHA-256 cryptographic integrity hashes, ready for 1-click broker dispatch without leaving traces on local staff computers.",
        key_improvements: [
          "Direct server-to-server Digio API integration with zero local desktop file saving",
          "Cryptographic SHA-256 integrity hashes verifying unaltered document provenance",
          "1-Click broker notification dispatch with secure, time-expiring presigned S3 links",
          "Audit trail logging every broker dossier transmission with timestamp and operator ID",
        ],
        screenshot_route: "/pending-users/",
        live_route: "/pending-users/",
        action_label: "Review KYC Queue",
        tech_stack: ["Digio Gateway API", "S3 Pre-signed Signer", "SHA-256 Hash Verifier"],
        code_snippet: "const dossier = await compileKycDossier(applicantId);\nconst sha256 = crypto.createHash('sha256').update(dossier.buffer).digest('hex');\nawait dispatchBrokerHandoff({ applicantId, sha256, s3Url: dossier.signedUrl });",
      },
    },
  ];

  onMount(async () => {
    loading = true;
    try {
      const data = await fetchSystemEvolutionDocs();
      if (data && data.metadata && data.chapters) {
        metadata = data.metadata;
        scorecards = data.scorecards || scorecards;
        chapters = data.chapters;
      }
    } catch (e) {
      console.warn("Using local SSOT catalog", e);
    } finally {
      loading = false;
    }
  });

  $: filteredChapters = chapters.filter((chapter) => {
    const matchesCategory =
      selectedCategory === "all" ||
      chapter.category.toLowerCase().replace(/[^a-z0-9]/g, "") ===
        selectedCategory.toLowerCase().replace(/[^a-z0-9]/g, "");

    const query = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !query ||
      chapter.title.toLowerCase().includes(query) ||
      chapter.category.toLowerCase().includes(query) ||
      chapter.tagline.toLowerCase().includes(query) ||
      chapter.executive_summary.toLowerCase().includes(query) ||
      (chapter.compliance_tags &&
        chapter.compliance_tags.some((t) => t.toLowerCase().includes(query)));

    return matchesCategory && matchesSearch;
  });

  const categories = [
    { id: "all", label: "All Tasks (7)" },
    { id: "securitycompliance", label: "Security & Auth" },
    { id: "accountoperations", label: "Demat & Approvals" },
    { id: "brokerintegration", label: "Broker (FCML)" },
    { id: "financialreconciliation", label: "Payment Audits" },
    { id: "riskmanagement", label: "Financial Health" },
    { id: "backofficereporting", label: "Multi-Reports" },
    { id: "kycverification", label: "Digio eKYC" },
  ];

  function printBriefing() {
    window.print();
  }

  function openLightbox(url) {
    lightboxImage = url;
  }

  function closeLightbox() {
    lightboxImage = null;
  }
</script>

<Page class="system-docs-page">
  <!-- Top Navbar (Hidden on Print) -->
  <Navbar
    large
    sliding={false}
    style="--f7-navbar-bg-color: #0f172a; --f7-navbar-text-color: #ffffff; --f7-navbar-link-color: #38bdf8;"
    class="no-print"
  >
    <NavLeft>
      <Link
        iconIos="f7:arrow_left"
        iconAurora="f7:arrow_left"
        iconMd="material:arrow_back"
        onClick={() => f7router.back()}
      />
    </NavLeft>

    <NavTitle>Broker & Back-Office Ops</NavTitle>
    <NavTitleLarge>Broker & Back-Office Operations</NavTitleLarge>

    <NavRight>
      <!-- View Mode Toggle -->
      <div class="view-mode-toggle">
        <button
          class="mode-btn {viewMode === 'boss' ? 'active' : ''}"
          on:click={() => (viewMode = 'boss')}
          title="Executive Boss View: Focus on ROI, Risk, Turnaround & Plain English"
        >
          👔 Executive View
        </button>
        <button
          class="mode-btn {viewMode === 'tech' ? 'active' : ''}"
          on:click={() => (viewMode = 'tech')}
          title="Technical Operator View: Focus on GraphQL, Code Snippets & Architecture"
        >
          💻 Technical View
        </button>
      </div>

      <!-- Export / Print Button -->
      <Button
        fill
        small
        color="teal"
        onClick={printBriefing}
        style="margin-left: 8px; font-weight: 600;"
      >
        🖨️ Export PDF Briefing
      </Button>
    </NavRight>
  </Navbar>

  <!-- Print-Only Official Document Header -->
  <div class="print-header only-print">
    <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #0f172a; padding-bottom: 12px; margin-bottom: 16px;">
      <div>
        <h1 style="margin: 0; font-size: 22px; color: #0f172a; text-transform: uppercase; font-weight: 800;">
          TRDR Systems Audit & Operations Briefing
        </h1>
        <p style="margin: 4px 0 0 0; font-size: 13px; color: #475569;">
          {metadata.subtitle}
        </p>
      </div>
      <div style="text-align: right; font-size: 11px; color: #64748b;">
        <div><strong>Version:</strong> {metadata.version} (SSOT)</div>
        <div><strong>Source Reference:</strong> {metadata.source_doc}</div>
        <div><strong>Generated Date:</strong> 27 October 2026</div>
        <div><strong>Classification:</strong> CONFIDENTIAL / SEBI COMPLIANCE</div>
      </div>
    </div>
  </div>

  <div class="page-content padding docs-content">
    <!-- Hero Banner & Executive Scorecard -->
    <div class="hero-container">
      <div class="hero-badge-row no-print">
        <span class="badge-pill new-badge">NEW</span>
        <span class="badge-pill version-badge">{metadata.version} SSOT</span>
        <span class="badge-pill ref-badge">Ref: {metadata.source_doc}</span>
        <span class="badge-pill status-badge">100% Automated</span>
      </div>

      <h1 class="hero-title">{metadata.title}</h1>
      <p class="hero-subtitle">{metadata.subtitle}</p>
      
      <p class="hero-desc">
        {metadata.executive_summary}
      </p>

      <!-- Executive Scorecards Grid -->
      <div class="scorecards-grid">
        {#each scorecards as card}
          <div class="scorecard-card">
            <div class="scorecard-header">
              <span class="scorecard-category">{card.category}</span>
              <span class="scorecard-change {card.direction}">{card.change}</span>
            </div>
            <div class="scorecard-value">{card.value}</div>
            <div class="scorecard-label">{card.label}</div>
            <div class="scorecard-desc">{card.description}</div>
          </div>
        {/each}
      </div>
    </div>

    <!-- Cross-Link Banner to F1F2 Autopilot Migration Guide -->
    <div
      class="f1f2-crosslink-banner no-print"
      on:click={() => navigateTo("/f1f2-migration-guide/")}
    >
      <div class="crosslink-content">
        <div class="crosslink-badge-row">
          <span class="badge-autopilot-sm">AUTOPILOT EVOLUTION</span>
          <span class="badge-contrast-sm">50 TABS ➔ ZERO TABS</span>
          <span style="background: rgba(255,255,255,0.1); color: #cbd5e1; font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 9999px;">GO DAEMON</span>
        </div>
        <div class="crosslink-title">Looking for the Trading Execution & Algorithmic Engine Evolution?</div>
        <div class="crosslink-sub">Explore the F1F2 to TRDR Autopilot Migration Guide: From 50 open browser tabs with setInterval to 24/7 autonomous cloud Go daemon, prime-stride fair queues, and unified 1,000+ UCC dealer terminal.</div>
      </div>
      <Button
        fill
        color="orange"
        onClick={() => f7router.navigate("/f1f2-migration-guide/")}
        style="font-weight: 700; border-radius: 8px; flex-shrink: 0;"
      >
        🚀 Open F1F2 Guide →
      </Button>
    </div>

    <!-- Interactive Filter & Search Controls (Hidden on Print) -->
    <div class="controls-bar no-print">
      <div class="category-pills">
        {#each categories as cat}
          <button
            class="cat-pill {selectedCategory === cat.id ? 'active' : ''}"
            on:click={() => (selectedCategory = cat.id)}
          >
            {cat.label}
          </button>
        {/each}
      </div>

      <div class="search-and-switch">
        <div class="search-box">
          <input
            type="text"
            placeholder="Search tasks, compliance tags, or workflows..."
            bind:value={searchQuery}
          />
          {#if searchQuery}
            <button class="clear-search" on:click={() => (searchQuery = '')}>✕</button>
          {/if}
        </div>

        <div class="view-indicator">
          Showing: <strong>{viewMode === 'boss' ? '👔 Executive (The Boss) View' : '💻 Technical Operator View'}</strong>
        </div>
      </div>
    </div>

    <!-- Chapter Stories (Before vs. Now Showcase) -->
    <div class="chapters-container">
      {#if filteredChapters.length === 0}
        <div class="empty-state">
          <h3>No matching tasks found</h3>
          <p>Try clearing your search query or choosing another category.</p>
          <Button outline small onClick={() => { selectedCategory = 'all'; searchQuery = ''; }}>
            Reset Filters
          </Button>
        </div>
      {/if}

      {#each filteredChapters as chapter (chapter.id)}
        <div class="chapter-card" id={chapter.id}>
          <!-- Chapter Header -->
          <div class="chapter-header">
            <div class="chapter-title-group">
              <div class="task-number-badge">TASK #{chapter.task_number}</div>
              <div>
                <h2 class="chapter-title">{chapter.title}</h2>
                <div class="chapter-tagline">{chapter.tagline}</div>
              </div>
            </div>

            <!-- Risk & Turnaround Metrics Header -->
            <div class="chapter-kpis">
              <div class="kpi-pill risk">
                <span class="kpi-label">Risk Reduction</span>
                <span class="kpi-contrast">
                  <span class="risk-tag before {chapter.risk_level_before.toLowerCase()}">{chapter.risk_level_before}</span>
                  <span class="arrow">→</span>
                  <span class="risk-tag now {chapter.risk_level_now.toLowerCase()}">{chapter.risk_level_now}</span>
                </span>
              </div>

              <div class="kpi-pill turnaround">
                <span class="kpi-label">Turnaround</span>
                <span class="kpi-contrast">
                  <span class="time before">{chapter.turnaround_before}</span>
                  <span class="arrow">→</span>
                  <span class="time now">{chapter.turnaround_now}</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Compliance Tags Row -->
          <div class="compliance-tags-row">
            <span class="compliance-label">Audited Standards:</span>
            {#each chapter.compliance_tags as tag}
              <span class="compliance-tag">🛡️ {tag}</span>
            {/each}
          </div>

          <!-- Summary Block (Executive vs Technical context) -->
          <div class="summary-box {viewMode}">
            {#if viewMode === 'boss'}
              <div class="boss-summary">
                <strong>Executive Takeaway:</strong> {chapter.executive_summary}
              </div>
            {:else}
              <div class="tech-summary">
                <strong>Technical Architecture:</strong> {chapter.technical_summary}
              </div>
            {/if}
          </div>

          <!-- Side-by-Side Before vs. Now Showcase -->
          <div class="comparison-grid">
            <!-- Left Column: The Old Manual Way -->
            <div class="comparison-col before-col">
              <div class="col-banner before-banner">
                <div class="banner-title">
                  <span class="banner-icon">⚠️</span> The Old Manual Way
                </div>
                <span class="banner-source">Legacy Operations Docx v2.1</span>
              </div>

              <div class="col-body">
                <h4 class="state-headline before-headline">{chapter.before.headline}</h4>
                <p class="state-desc">{chapter.before.description}</p>

                <!-- Media Attachment / WhatsApp Screenshot (Task 3 Feature Highlight) -->
                {#if chapter.before.image_url}
                  <div class="media-container">
                    <div class="media-preview-wrapper" on:click={() => openLightbox(chapter.before.image_url)}>
                      <img
                        src={chapter.before.image_url}
                        alt={chapter.before.image_caption}
                        class="evidence-img"
                        loading="lazy"
                      />
                      <div class="media-zoom-overlay">
                        <span>🔍 Click to Zoom Actual WhatsApp Evidence</span>
                      </div>
                    </div>
                    <div class="media-caption">
                      <strong>Documented Evidence:</strong> {chapter.before.image_caption}
                    </div>
                  </div>
                {/if}

                <!-- Pain Points List -->
                <div class="points-section">
                  <div class="points-heading">Operational Pain Points & Hazards:</div>
                  <ul class="points-list before-list">
                    {#each chapter.before.pain_points as pain}
                      <li>
                        <span class="point-icon">❌</span>
                        <span class="point-text">{pain}</span>
                      </li>
                    {/each}
                  </ul>
                </div>

                <!-- Technical Code Snippet (Shown in Technical View) -->
                {#if viewMode === 'tech' && chapter.before.code_snippet}
                  <div class="code-block legacy-code">
                    <div class="code-title">Legacy Manual Request / Payload:</div>
                    <pre><code>{chapter.before.code_snippet}</code></pre>
                  </div>
                {/if}
              </div>
            </div>

            <!-- Right Column: The Modern Automated Way -->
            <div class="comparison-col now-col">
              <div class="col-banner now-banner">
                <div class="banner-title">
                  <span class="banner-icon">✨</span> The Modern Automated Way
                </div>
                <span class="banner-source">TRDR Production Platform</span>
              </div>

              <div class="col-body">
                <h4 class="state-headline now-headline">{chapter.now.headline}</h4>
                <p class="state-desc">{chapter.now.description}</p>

                <!-- Key Improvements List -->
                <div class="points-section">
                  <div class="points-heading">Automated Capabilities & Safeguards:</div>
                  <ul class="points-list now-list">
                    {#each chapter.now.key_improvements as imp}
                      <li>
                        <span class="point-icon">✅</span>
                        <span class="point-text">{imp}</span>
                      </li>
                    {/each}
                  </ul>
                </div>

                <!-- Tech Stack Badges -->
                <div class="tech-stack-row">
                  <span class="tech-stack-label">Under the Hood:</span>
                  {#each chapter.now.tech_stack as tech}
                    <span class="tech-pill">{tech}</span>
                  {/each}
                </div>

                <!-- Technical Code Snippet (Shown in Technical View) -->
                {#if viewMode === 'tech' && chapter.now.code_snippet}
                  <div class="code-block modern-code">
                    <div class="code-title">Production Automated Resolver / Handler:</div>
                    <pre><code>{chapter.now.code_snippet}</code></pre>
                  </div>
                {/if}

                <!-- Live Interactive Operational Launch Button (Hidden on Print) -->
                <div class="action-btn-row no-print">
                  <Button
                    fill
                    color="blue"
                    onClick={() => f7router.navigate(chapter.now.live_route)}
                    style="font-weight: 600; border-radius: 8px; height: 38px; display: inline-flex; align-items: center; gap: 6px;"
                  >
                    🚀 {chapter.now.action_label} →
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      {/each}
    </div>

    <!-- Official Document Footer -->
    <div class="docs-footer">
      <div class="footer-meta">
        <strong>TRDR Single Source of Truth Documentation Hub</strong> — Maintained dynamically in backend services.
      </div>
      <div class="footer-links no-print">
        <Link onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          ▲ Back to Top
        </Link>
        <span class="sep">•</span>
        <Link onClick={() => f7router.navigate('/admin-home/')}>
          Admin Home
        </Link>
        <span class="sep">•</span>
        <Link onClick={printBriefing}>
          Print Executive Briefing
        </Link>
      </div>
    </div>
  </div>

  <!-- Lightbox Modal for WhatsApp Screenshot Evidence -->
  {#if lightboxImage}
    <div class="lightbox-backdrop" on:click={closeLightbox}>
      <div class="lightbox-content" on:click|stopPropagation>
        <div class="lightbox-header">
          <div style="font-weight: 700; color: #fff; font-size: 15px;">
            Document Evidence: WhatsApp Chat with Amal TRDR (from Docx v2.1)
          </div>
          <button class="lightbox-close" on:click={closeLightbox}>✕ Close</button>
        </div>
        <div class="lightbox-body">
          <img src={lightboxImage} alt="WhatsApp Evidence Full Preview" class="lightbox-img" />
          <p class="lightbox-caption">
            Real screenshot extracted from <code>How to_ TRDR Admin API's (1).docx</code> showing back-office operator requesting GraphQL script on WhatsApp Web and engineering providing mutation syntax with live JWT auth tokens in URLs.
          </p>
        </div>
      </div>
    </div>
  {/if}
</Page>

<style>
  :global(.system-docs-page) {
    background-color: #f8fafc;
    color: #1e293b;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  }

  /* View Mode Toggle Button in Navbar */
  .view-mode-toggle {
    display: inline-flex;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 2px;
    border: 1px solid rgba(255, 255, 255, 0.15);
  }

  .mode-btn {
    background: transparent;
    border: none;
    color: #94a3b8;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.15s ease-in-out;
  }

  .mode-btn.active {
    background: #2563eb;
    color: #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  /* Hero Container */
  .hero-container {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    color: #ffffff;
    border-radius: 16px;
    padding: 32px;
    box-shadow: 0 12px 30px -8px rgba(15, 23, 42, 0.25);
    margin-bottom: 24px;
    border: 1px solid #334155;
  }

  .hero-badge-row {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
    flex-wrap: wrap;
    align-items: center;
  }

  .badge-pill {
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 9999px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .new-badge {
    background: #2563eb;
    color: #ffffff;
  }

  .version-badge {
    background: rgba(255, 255, 255, 0.12);
    color: #cbd5e1;
  }

  .ref-badge {
    background: rgba(255, 255, 255, 0.08);
    color: #94a3b8;
  }

  .status-badge {
    background: #059669;
    color: #ffffff;
  }

  .hero-title {
    font-size: 26px;
    font-weight: 800;
    margin: 0 0 8px 0;
    letter-spacing: -0.02em;
    color: #ffffff;
    line-height: 1.25;
  }

  .hero-subtitle {
    font-size: 15px;
    font-weight: 500;
    color: #38bdf8;
    margin: 0 0 14px 0;
    line-height: 1.4;
  }

  .hero-desc {
    font-size: 14px;
    color: #cbd5e1;
    line-height: 1.6;
    max-width: 900px;
    margin: 0 0 24px 0;
  }

  /* Scorecards Grid */
  .scorecards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    padding-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .scorecard-card {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 12px;
    padding: 16px;
    backdrop-filter: blur(8px);
    transition: transform 0.2s ease;
  }

  .scorecard-card:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.09);
  }

  /* Crosslink Banner */
  .f1f2-crosslink-banner {
    background: linear-gradient(135deg, #0b0f19 0%, #1e1b4b 60%, #0f172a 100%);
    border: 1px solid #3b4252;
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
    transition: all 0.2s ease;
  }

  .f1f2-crosslink-banner:hover {
    border-color: #f97316;
    transform: translateY(-2px);
  }

  .crosslink-badge-row {
    display: flex;
    gap: 8px;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }

  .badge-autopilot-sm {
    background: #ea580c;
    color: #fff;
    font-size: 10px;
    font-weight: 800;
    padding: 2px 8px;
    border-radius: 9999px;
    text-transform: uppercase;
  }

  .badge-contrast-sm {
    background: #2563eb;
    color: #fff;
    font-size: 10px;
    font-weight: 800;
    padding: 2px 8px;
    border-radius: 9999px;
    text-transform: uppercase;
  }

  .crosslink-title {
    font-size: 15px;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 4px;
  }

  .crosslink-sub {
    font-size: 12.5px;
    color: #94a3b8;
    line-height: 1.4;
  }

  .crosslink-action-btn {
    background: #ea580c;
    color: #ffffff;
    border: none;
    font-size: 12.5px;
    font-weight: 700;
    padding: 8px 16px;
    border-radius: 8px;
    white-space: nowrap;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(234, 88, 12, 0.4);
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .crosslink-action-btn:hover {
    background: #f97316;
  }

  .scorecard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .scorecard-category {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .scorecard-change {
    font-size: 11px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .scorecard-change.up {
    background: rgba(16, 185, 129, 0.2);
    color: #34d399;
  }

  .scorecard-change.down {
    background: rgba(56, 189, 248, 0.2);
    color: #38bdf8;
  }

  .scorecard-value {
    font-size: 28px;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 4px;
    letter-spacing: -0.02em;
  }

  .scorecard-label {
    font-size: 13px;
    font-weight: 700;
    color: #e2e8f0;
    margin-bottom: 4px;
  }

  .scorecard-desc {
    font-size: 11px;
    color: #94a3b8;
    line-height: 1.4;
  }

  /* Controls Bar */
  .controls-bar {
    background: #ffffff;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
    border: 1px solid #e2e8f0;
    margin-bottom: 24px;
  }

  .category-pills {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 8px;
    margin-bottom: 12px;
  }

  .cat-pill {
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    color: #475569;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 9999px;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s ease;
  }

  .cat-pill:hover {
    background: #e2e8f0;
    color: #1e293b;
  }

  .cat-pill.active {
    background: #0f172a;
    border-color: #0f172a;
    color: #ffffff;
  }

  .search-and-switch {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  .search-box {
    position: relative;
    flex: 1;
    min-width: 260px;
    max-width: 420px;
  }

  .search-box input {
    width: 100%;
    padding: 8px 32px 8px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 13px;
    background: #f8fafc;
    box-sizing: border-box;
  }

  .search-box input:focus {
    outline: none;
    border-color: #2563eb;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  .clear-search {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    font-size: 12px;
  }

  .view-indicator {
    font-size: 12px;
    color: #64748b;
  }

  /* Chapter Card */
  .chapter-card {
    background: #ffffff;
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
    margin-bottom: 24px;
    overflow: hidden;
  }

  .chapter-header {
    padding: 20px 24px;
    border-bottom: 1px solid #f1f5f9;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 16px;
    background: #fafafa;
  }

  .chapter-title-group {
    display: flex;
    gap: 14px;
    align-items: flex-start;
  }

  .task-number-badge {
    background: #0f172a;
    color: #ffffff;
    font-size: 12px;
    font-weight: 800;
    padding: 4px 10px;
    border-radius: 8px;
    white-space: nowrap;
    margin-top: 2px;
    letter-spacing: 0.05em;
  }

  .chapter-title {
    margin: 0 0 4px 0;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
  }

  .chapter-tagline {
    font-size: 13px;
    color: #64748b;
  }

  .chapter-kpis {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .kpi-pill {
    background: #f1f5f9;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 140px;
  }

  .kpi-label {
    font-size: 10px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .kpi-contrast {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 700;
  }

  .kpi-contrast .arrow {
    color: #94a3b8;
  }

  .risk-tag {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
  }

  .risk-tag.critical {
    background: #fee2e2;
    color: #dc2626;
  }

  .risk-tag.high {
    background: #fef3c7;
    color: #d97706;
  }

  .risk-tag.zero-risk,
  .risk-tag.low-risk {
    background: #d1fae5;
    color: #059669;
  }

  .time.before {
    color: #dc2626;
    text-decoration: line-through;
    opacity: 0.8;
  }

  .time.now {
    color: #059669;
  }

  .compliance-tags-row {
    padding: 10px 24px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .compliance-label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
  }

  .compliance-tag {
    font-size: 11px;
    background: #eff6ff;
    color: #1d4ed8;
    padding: 2px 8px;
    border-radius: 6px;
    font-weight: 600;
    border: 1px solid #dbeafe;
  }

  /* Summary Box */
  .summary-box {
    padding: 14px 24px;
    font-size: 13px;
    line-height: 1.5;
    border-bottom: 1px solid #f1f5f9;
  }

  .summary-box.boss {
    background: #f0fdf4;
    color: #166534;
    border-left: 4px solid #16a34a;
  }

  .summary-box.tech {
    background: #f8fafc;
    color: #334155;
    border-left: 4px solid #2563eb;
  }

  /* Comparison Grid (Side-by-Side) */
  .comparison-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
  }

  @media (max-width: 900px) {
    .comparison-grid {
      grid-template-columns: 1fr;
    }
  }

  .comparison-col {
    padding: 20px 24px;
  }

  .before-col {
    background: #fffafa;
    border-right: 1px solid #fee2e2;
  }

  .now-col {
    background: #f0fdf410;
  }

  .col-banner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-radius: 8px;
    margin-bottom: 14px;
  }

  .before-banner {
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
  }

  .now-banner {
    background: #dcfce7;
    color: #166534;
    border: 1px solid #bbf7d0;
  }

  .banner-title {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .banner-source {
    font-size: 11px;
    opacity: 0.8;
  }

  .state-headline {
    margin: 0 0 8px 0;
    font-size: 15px;
    font-weight: 700;
  }

  .before-headline {
    color: #991b1b;
  }

  .now-headline {
    color: #166534;
  }

  .state-desc {
    font-size: 13px;
    color: #475569;
    line-height: 1.5;
    margin: 0 0 16px 0;
  }

  /* Media Preview for WhatsApp Evidence */
  .media-container {
    background: #ffffff;
    border: 1px solid #fca5a5;
    border-radius: 10px;
    padding: 10px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(220, 38, 38, 0.08);
  }

  .media-preview-wrapper {
    position: relative;
    cursor: pointer;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    max-height: 240px;
  }

  .evidence-img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
    object-position: top;
    transition: transform 0.25s ease;
  }

  .media-preview-wrapper:hover .evidence-img {
    transform: scale(1.02);
  }

  .media-zoom-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, transparent 100%);
    color: #ffffff;
    font-size: 11px;
    font-weight: 600;
    padding: 12px 10px 8px 10px;
    text-align: center;
  }

  .media-caption {
    font-size: 11px;
    color: #64748b;
    margin-top: 8px;
    line-height: 1.4;
  }

  /* Points List */
  .points-section {
    margin-bottom: 16px;
  }

  .points-heading {
    font-size: 12px;
    font-weight: 700;
    color: #334155;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .points-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .points-list li {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    font-size: 13px;
    line-height: 1.45;
    margin-bottom: 8px;
  }

  .before-list li {
    color: #7f1d1d;
  }

  .now-list li {
    color: #14532d;
  }

  .point-icon {
    font-size: 12px;
    margin-top: 2px;
  }

  .point-text {
    flex: 1;
  }

  /* Tech Stack Row */
  .tech-stack-row {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 16px;
    padding: 8px 12px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
  }

  .tech-stack-label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
  }

  .tech-pill {
    background: #e0f2fe;
    color: #0369a1;
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: 600;
    font-family: monospace;
  }

  /* Code Blocks */
  .code-block {
    background: #0f172a;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 16px;
    overflow-x: auto;
  }

  .code-title {
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    margin-bottom: 6px;
    text-transform: uppercase;
    font-family: monospace;
  }

  .code-block pre {
    margin: 0;
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
    font-size: 12px;
    line-height: 1.45;
  }

  .legacy-code code {
    color: #f87171;
  }

  .modern-code code {
    color: #4ade80;
  }

  .action-btn-row {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #e2e8f0;
  }

  /* Footer */
  .docs-footer {
    padding: 24px;
    text-align: center;
    border-top: 1px solid #e2e8f0;
    color: #64748b;
    font-size: 13px;
    margin-top: 32px;
  }

  .footer-links {
    margin-top: 8px;
    display: flex;
    justify-content: center;
    gap: 8px;
    align-items: center;
  }

  .footer-links .sep {
    color: #cbd5e1;
  }

  /* Lightbox Modal */
  .lightbox-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 23, 42, 0.85);
    backdrop-filter: blur(6px);
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }

  .lightbox-content {
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 12px;
    max-width: 900px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  }

  .lightbox-header {
    padding: 16px 20px;
    border-bottom: 1px solid #334155;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .lightbox-close {
    background: #334155;
    border: none;
    color: #ffffff;
    padding: 6px 14px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
  }

  .lightbox-close:hover {
    background: #475569;
  }

  .lightbox-body {
    padding: 20px;
    text-align: center;
  }

  .lightbox-img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    border: 1px solid #334155;
  }

  .lightbox-caption {
    margin-top: 12px;
    color: #94a3b8;
    font-size: 13px;
    line-height: 1.5;
    text-align: left;
  }

  .lightbox-caption code {
    background: rgba(255, 255, 255, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    color: #38bdf8;
  }

  .empty-state {
    text-align: center;
    padding: 40px 20px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
  }

  /* Print Stylesheet (Phase 4) */
  .only-print {
    display: none;
  }

  @media print {
    .no-print {
      display: none !important;
    }

    .only-print {
      display: block !important;
    }

    :global(body),
    :global(.page),
    :global(.page-content),
    .system-docs-page {
      background: #ffffff !important;
      color: #000000 !important;
      font-size: 10pt !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .hero-container {
      background: #f8fafc !important;
      color: #000000 !important;
      border: 1px solid #cbd5e1 !important;
      box-shadow: none !important;
      padding: 16px !important;
      page-break-inside: avoid;
    }

    .hero-title {
      color: #0f172a !important;
      font-size: 18pt !important;
    }

    .hero-subtitle {
      color: #0369a1 !important;
      font-size: 11pt !important;
    }

    .hero-desc {
      color: #334155 !important;
      font-size: 9pt !important;
    }

    .scorecard-card {
      background: #ffffff !important;
      border: 1px solid #cbd5e1 !important;
      color: #000000 !important;
    }

    .scorecard-value {
      color: #0f172a !important;
    }

    .scorecard-label {
      color: #334155 !important;
    }

    .chapter-card {
      border: 1px solid #cbd5e1 !important;
      box-shadow: none !important;
      margin-bottom: 20px !important;
      page-break-inside: avoid !important;
    }

    .chapter-header {
      background: #f1f5f9 !important;
    }

    .before-col {
      background: #fff5f5 !important;
      border-right: 1px solid #cbd5e1 !important;
    }

    .now-col {
      background: #f0fdf4 !important;
    }

    .comparison-grid {
      grid-template-columns: 1fr 1fr !important;
    }

    .media-preview-wrapper {
      max-height: 180px !important;
    }
  }
</style>
